/* eslint-disable max-lines */
// 9fbef606107a605d69c0edbcd8029e5d
import { getSessionStorage, parseBoolean } from '@tcp/core/src/utils';
import isEmpty from 'lodash/isEmpty';
import { formValueSelector, getFormValues, getFormSyncErrors, getFormMeta } from 'redux-form';
import { createSelector } from 'reselect';
import { isApplePayEligible } from '@tcp/core/src/utils/utils';
import CREDIT_CARD_CONSTANTS from '@tcp/core/src/components/features/CnC/Checkout/organisms/BillingPaymentForm/container/CreditCard.constants';
import CARTPAGE_CONSTANTS from '@tcp/core/src/components/features/CnC/CartItemTile/CartItemTile.constants';
import {
  getGrandTotal,
  getGiftCardsTotal,
} from '@tcp/core/src/components/features/CnC/common/organism/OrderLedger/container/orderLedger.selector';
import {
  getAfterPayMinOrderAmount,
  getAfterPayMaxOrderAmount,
  getIsVMPBossEnabled,
  getIsDefaultApplePayAbTest,
} from '@tcp/core/src/reduxStore/selectors/session.selectors';
import BAGPAGE_SELECTORS, {
  getOrderType,
  isECOMStatus,
  isPickuOrderStatus,
} from '../../BagPage/container/BagPage.selectors';

import {
  getProductDetails,
  getCartOrderList,
} from '../../CartItemTile/container/CartItemTile.selectors';
import CheckoutSelector, {
  isGuest,
  getPickupValues,
  getLoginV2FlowEnabled,
} from '../../Checkout/container/Checkout.selector';
import { SHIPPING_FORM_NAME } from './Shipping/Shipping.constants';
import {
  CARD_TYPES,
  SPC_SECTION,
  PAYMENT_METHOD_AFTERPAY,
  PAYMENT_METHOD_VENMO,
  ADDRESS_TYPE,
} from './SinglePageCheckout.constants';
import {
  convertToUserAddressFormat,
  convertBillingToUserAddress,
  unionAddresses,
} from './SinglePageCheckout.utils';
import { getAddressListState } from '../../../account/AddressBook/container/AddressBook.selectors';
import CheckoutConstants, {
  DUMMY_ADDRESS_VALUES_FOR_APPLE_PAY,
  PAYMENT_METHODS_WITHOUT_CARD_NUMBER,
} from '../../Checkout/Checkout.constants';

const getState = (state) => state;

const checkIsSinglePageCheckout = createSelector(
  getState,
  (state) => state.singlePageCheckout && state.singlePageCheckout.isSPC
);

export const checkIsExpressCallDone = createSelector(
  getState,
  (state) => state.singlePageCheckout && state.singlePageCheckout.isExpressCallDone
);

export const getShouldShowShipmentMethodsCollapsed = createSelector(
  getState,
  (state) =>
    state.singlePageCheckout && state.singlePageCheckout.isShouldShowShippingMethodsInCollapsed
);

export const checkIsNonCachedCartDataCallDone = createSelector(
  getState,
  (state) => state.singlePageCheckout && state.singlePageCheckout.isNonCachedCartDataCallDone
);

export const getPageData = createSelector(getState, (state) => state.pageData);

export const getSinglePageCheckoutPageState = createSelector(
  getState,
  (state) => state.singlePageCheckout
);

export const getSubmitOrderButtonState = createSelector(
  getState,
  (state) => state.singlePageCheckout && state.singlePageCheckout?.submitOrderButtonState
);

const getSectionState = (state, sectionId) => state?.singlePageCheckout[sectionId];

export const getSinglePageCheckoutSectionState = () => {
  return createSelector(getSectionState, (sectionSate) => sectionSate);
};

const isPaymentMethodExpanded = (state) => {
  const paymentSectionState = getSectionState(state, SPC_SECTION.PAYMENT);
  return paymentSectionState?.isPaymentMethodExpanded || false;
};

const isCardFormExpanded = (state) => {
  const paymentSectionState = getSectionState(state, SPC_SECTION.PAYMENT);
  return paymentSectionState?.isExpanded || false;
};

export const shouldShowErrorOnSpc = (state) => {
  return !(isPaymentMethodExpanded(state) || isCardFormExpanded(state));
};

export const getPaymentId = (state) => {
  return state.CartPageReducer.getIn(['orderDetails', 'checkout', 'billing', 'paymentMethod']);
};

export const getIsAfterpayPayment = createSelector(getPaymentId, (paymentMethod) => {
  return paymentMethod === PAYMENT_METHOD_AFTERPAY;
});

export const getIsVenmoPayment = createSelector(getPaymentId, (paymentMethod) => {
  return paymentMethod === PAYMENT_METHOD_VENMO;
});

export const getOrderTotalAmount = createSelector(
  [getGrandTotal, getGiftCardsTotal],
  (grandTotal, giftCardsTotal) => {
    return grandTotal - giftCardsTotal;
  }
);
export const getIsExtendedPreOrderForSPC = (state) => {
  return state.singlePageCheckout.isExtendedPreOrderForSpc;
};
export const getIsAppleMobileWalletSetup = (state) => {
  return state.singlePageCheckout.isWalletSetup;
};

export const getCvvValidationFormData = (state) => {
  return {
    values: getFormValues('checkoutCardCvvValidation')(state),
    syncErrors: getFormSyncErrors('checkoutCardCvvValidation')(state),
    meta: getFormMeta('checkoutCardCvvValidation')(state),
  };
};

export const getOrderProducts = createSelector([BAGPAGE_SELECTORS.getOrderItems], (items) => {
  return !isEmpty(items) ? items.map((item) => getProductDetails(item)) : [];
});

export const getProductsItems = createSelector(getOrderProducts, (orderProducts) => {
  // eslint-disable-next-line no-underscore-dangle
  return orderProducts?._tail?.array;
});

export const getShipmentMethodsFields = createSelector(getState, (state) => {
  const selector = formValueSelector(SHIPPING_FORM_NAME);
  return selector(state, 'shipmentMethods');
});

export const getSelectedShipmentId = createSelector(
  getShipmentMethodsFields,
  (shipmentMethodsFields) => shipmentMethodsFields && shipmentMethodsFields.shippingMethodId
);

// eslint-disable-next-line sonarjs/cognitive-complexity
export const getSavedContactDetails = () => {
  let emailAddress = '';
  let phoneNumber = '';
  try {
    let sessionContactDetails = getSessionStorage('CONTACT_FORM');
    if (!isEmpty(sessionContactDetails)) {
      sessionContactDetails = JSON.parse(sessionContactDetails);
      sessionContactDetails.emailAddress =
        sessionContactDetails.emailAddress === DUMMY_ADDRESS_VALUES_FOR_APPLE_PAY.email
          ? ''
          : sessionContactDetails.emailAddress;
      sessionContactDetails.phoneNumber =
        sessionContactDetails.phoneNumber === DUMMY_ADDRESS_VALUES_FOR_APPLE_PAY.phone
          ? ''
          : sessionContactDetails.phoneNumber;
      emailAddress = sessionContactDetails?.emailAddress;
      phoneNumber = sessionContactDetails?.phoneNumber;
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Error in getSavedContactDetails', e);
  }
  return { emailAddress, phoneNumber };
};

export const checkIsContactDetailsSaved = createSelector(getState, (state) => {
  const { emailAddress, phoneNumber } = getSavedContactDetails();
  const contactData = getSinglePageCheckoutSectionState()(state, SPC_SECTION.CONTACT);
  const { emailAddress: savedEmail, phoneNumber: savedPhoneNo } = contactData?.savedContactInfo;
  const { emailAddress: orderEmail, phoneNumber: orderPhoneNo } =
    CheckoutSelector.getShippingPhoneAndEmail(state) || {};
  if (
    (!isEmpty(orderPhoneNo) && !isEmpty(orderEmail)) ||
    (!isEmpty(savedPhoneNo) && !isEmpty(savedEmail))
  ) {
    return true;
  }
  const isGuestUser = isGuest(state);
  return !isEmpty(phoneNumber) && (!isEmpty(emailAddress) || !isGuestUser);
});

export const getBossStatus = createSelector(
  (state) => BAGPAGE_SELECTORS.getOrderItems(state),
  (list) => {
    const orderTypes = [];
    if (list && list !== 0) {
      list.forEach((item) => {
        orderTypes.push(item.getIn(['productInfo', 'orderType']));
      });
    } else {
      return orderTypes;
    }

    return orderTypes.every((item) => item === 'BOSS') || false;
  }
);

// eslint-disable-next-line sonarjs/cognitive-complexity
export const getBossGCAndPreOrderCase = createSelector(getState, (state) => {
  const list = getCartOrderList(state);
  const isVMPBOSSEnabled = getIsVMPBossEnabled(state);
  const { BOSS } = CARTPAGE_CONSTANTS;
  let isBossItem = false;
  let isGiftCard = false;
  let isPreOrder = false;
  let multiPack = false;
  let bossInEligible = false;
  if (list?.size > 0) {
    list.forEach((item) => {
      if (item?.getIn(['miscInfo', 'orderItemType']) === BOSS) {
        isBossItem = true;
        if (item?.getIn(['productInfo', 'isGiftCard'])) {
          isGiftCard = true;
        }
        if (item?.getIn(['itemInfo', 'preOrderDate'])) {
          isPreOrder = true;
        }
        if (!item?.getIn(['miscInfo', 'isBossEligible'])) {
          bossInEligible = true;
        }
        if (item?.getIn(['productInfo', 'multiPack'])) {
          multiPack = isVMPBOSSEnabled
            ? !item?.getIn(['miscInfo', 'isBossTileEligible'])
            : !isVMPBOSSEnabled;
        }
      }
    });
  }
  return isBossItem && (isGiftCard || multiPack || isPreOrder || bossInEligible);
});

export const getBossAndPreOrder = createSelector(getState, (state) => {
  const list = getCartOrderList(state);
  const { BOSS } = CARTPAGE_CONSTANTS;
  let isBossItem = false;
  let isPreOrder = false;
  if (list?.size > 0) {
    list.forEach((item) => {
      if (
        item?.getIn(['miscInfo', 'orderItemType']) === BOSS &&
        item?.getIn(['itemInfo', 'preOrderDate'])
      ) {
        isBossItem = true;
        isPreOrder = true;
      }
    });
  }
  return isBossItem && isPreOrder;
});

export const getBossAndGC = createSelector(getState, (state) => {
  const list = getCartOrderList(state);
  const { BOSS } = CARTPAGE_CONSTANTS;
  let isBossItem = false;
  let isGiftCard = false;
  if (list?.size > 0) {
    list.forEach((item) => {
      if (
        item?.getIn(['miscInfo', 'orderItemType']) === BOSS &&
        item?.getIn(['productInfo', 'isGiftCard'])
      ) {
        isBossItem = true;
        isGiftCard = true;
      }
    });
  }
  return isBossItem && isGiftCard;
});

export const getBossInEligible = createSelector(getState, (state) => {
  const list = getCartOrderList(state);
  const { BOSS } = CARTPAGE_CONSTANTS;
  let isBossItem = false;
  let isBossinEligible = false;
  if (list?.size > 0) {
    list.forEach((item) => {
      if (
        item?.getIn(['miscInfo', 'orderItemType']) === BOSS &&
        !item?.getIn(['miscInfo', 'isBossEligible'])
      ) {
        isBossItem = true;
        isBossinEligible = true;
      }
    });
  }
  return isBossItem && isBossinEligible;
});

export const checkIsPickupDetailsSaved = createSelector(getState, (state) => {
  const pickUpContactPerson = getPickupValues(state);
  const isPickUpOrder = isPickuOrderStatus(state);
  if (isPickUpOrder) {
    return !isEmpty(pickUpContactPerson?.firstName) && !isEmpty(pickUpContactPerson?.lastName);
  }
  return true;
});

export const getShippingSectionSavedStatus = createSelector(
  [CheckoutSelector.getShippingDestinationValues, getLoginV2FlowEnabled],
  (savedShippingValues, isLoginV2FlowEnabled) => {
    const { address, method } = savedShippingValues;
    const isShippingAddressSaved = !!address?.addressId;
    const isShipmentMethodSaved = !!method?.shippingMethodId;
    const isShippingFormSaved = isLoginV2FlowEnabled ? !!address?.phone : isShippingAddressSaved;
    return {
      isShippingAddressSaved,
      isShipmentMethodSaved,
      isShippingSectionSaved: isShippingFormSaved && isShipmentMethodSaved,
    };
  }
);

export const checkIsShippingDetailsSaved = createSelector(getState, (state) => {
  const isEcomOrder = isECOMStatus(state);
  const { isShippingSectionSaved } = getShippingSectionSavedStatus(state);
  if (isEcomOrder) {
    return isShippingSectionSaved;
  }
  return true;
});

export const getAllShippingAddressList = createSelector(
  [CheckoutSelector.getShippingAddressList, CheckoutSelector.getShippingAddress],
  (userAddresses, shippingAddress) => {
    const formattedShippingAddress = convertToUserAddressFormat(shippingAddress);
    const filteredUserAddresses = [...userAddresses];
    if (!filteredUserAddresses.length) {
      // If the address book is empty, do not show list
      return [];
    }
    const selectedAddressId = formattedShippingAddress?.addressId;
    return (
      selectedAddressId
        ? unionAddresses([formattedShippingAddress], filteredUserAddresses)
        : filteredUserAddresses
    ).sort((a, b) => {
      if (
        a.addressId === selectedAddressId ||
        (a.primary === 'true' && b.addressId !== selectedAddressId) ||
        (a.primary === 'true' && !selectedAddressId)
      ) {
        return -1;
      }
      return a.addressId > b.addressId;
    });
  }
);

export const getDefaultAddress = createSelector([getAllShippingAddressList], (userAddress) => {
  return (userAddress && userAddress.length && userAddress[0]) || {};
});

export const getAllBillingAddressList = createSelector(
  [getAddressListState, CheckoutSelector.getBillingAddress],
  (userAddresses, billingAddress) => {
    const formattedBillingAddresses = convertBillingToUserAddress(billingAddress);
    const userAddressesArr = userAddresses && userAddresses.size > 0 ? userAddresses : [];
    const filteredUserAddresses = [...userAddressesArr]?.filter(
      (a) => a.c_addressType && a.c_addressType?.toLowerCase()?.includes(ADDRESS_TYPE.BILLING)
    );
    if (!filteredUserAddresses.length) {
      // If the address book is empty, do not show list
      return [];
    }
    const selectedAddressId = formattedBillingAddresses?.addressId;
    return (
      selectedAddressId
        ? unionAddresses([formattedBillingAddresses], filteredUserAddresses)
        : filteredUserAddresses
    )
      .map((a) => ({
        ...a,
        addressLine1: a.addressLine?.[0],
        addressLine2: a.addressLine?.[1],
      }))
      .sort((a, b) => {
        if (a.addressId === selectedAddressId) return -1;
        return a.addressId > b.addressId;
      });
  }
);

export const cvvModuleXContent = createSelector(
  getSinglePageCheckoutPageState,
  (state) => state?.cvvModuleXContent
);

export const giftDetailsModalModuleXContent = createSelector(
  getSinglePageCheckoutPageState,
  (state) => state?.giftDetailsModalModuleXContent
);

export const giftDetailsGymModalModuleXContent = createSelector(
  getSinglePageCheckoutPageState,
  (state) => state?.giftDetailsGymModalModuleXContent
);

export const giftDetailsSnjModalModuleXContent = createSelector(
  getSinglePageCheckoutPageState,
  (state) => state?.giftDetailsSnjModalModuleXContent
);

export const giftDetailsPjpModalModuleXContent = createSelector(
  getSinglePageCheckoutPageState,
  (state) => state?.giftDetailsPjpModalModuleXContent
);

export const isCvvVisibleOnViewMode = createSelector(
  getSinglePageCheckoutPageState,
  (state) => state.isCvvVisibleOnViewMode
);

export const showAddressVerification = createSelector(
  getSinglePageCheckoutPageState,
  (state) => state.showAddressVerification
);

const sectionSaved = createSelector(
  [getShippingSectionSavedStatus, getOrderType, getPickupValues],
  (isShippingSectionSavedStatus, orderType, pickupValue) => {
    const isPickupSectionSaved = !isEmpty(pickupValue?.firstName);
    // eslint-disable-next-line no-nested-ternary
    return orderType === 'PURE_BOSS' || orderType === 'PURE_BOPIS'
      ? isPickupSectionSaved
      : orderType === 'ECOM_MIX'
      ? isPickupSectionSaved && isShippingSectionSavedStatus?.isShippingAddressSaved
      : isShippingSectionSavedStatus?.isShippingAddressSaved;
  }
);

const getIsBillingAddressSaved = (allowWithoutCardNumberPayment, billingAddress) => {
  return allowWithoutCardNumberPayment
    ? !!billingAddress?.addressLine1
    : !!billingAddress?.addressId;
};
export const checkIsPaymentSectionSaved = createSelector(
  [
    CheckoutSelector.getBillingValues,
    CheckoutSelector.getIsPaymentDisabled,
    CheckoutSelector.getIsPayPalSelected,
    CheckoutSelector.getIsAfterPayInProgress,
    CheckoutSelector.isVenmoPaymentInProgress,
    getGrandTotal,
    getGiftCardsTotal,
    getAfterPayMaxOrderAmount,
    getAfterPayMinOrderAmount,
    getShippingSectionSavedStatus,
    getPaymentId,
    getIsDefaultApplePayAbTest,
    getIsExtendedPreOrderForSPC,
    checkIsPickupDetailsSaved,
    sectionSaved,
  ],
  (
    savedBillingValues,
    isPaymentDisabled,
    isPayPalSelected,
    isAfterPayInProgress,
    isVenmoPaymentInProgress,
    grandTotal,
    giftCardTotal,
    afterPayMaxOrderAmount,
    afterPayMinOrderAmount,
    isShippingSectionSavedStatus,
    billingPaymentMethod,
    defaultApplePay,
    isExtendedPreOrder,
    isPickupSectionSaved,
    sectionSavedSPC
    // eslint-disable-next-line max-params
  ) => {
    const orderTotal = grandTotal - giftCardTotal;
    const { address: billingAddress, billing: cardDetails, paymentMethod } = savedBillingValues;
    const allowWithoutCardNumberPayment =
      PAYMENT_METHODS_WITHOUT_CARD_NUMBER.includes(paymentMethod);
    const isBillingAddressSaved = getIsBillingAddressSaved(
      allowWithoutCardNumberPayment,
      billingAddress
    );
    const isCardDetailsSaved = !!cardDetails?.cardNumber;
    const isApplePayAcceptable = isApplePayEligible();
    let isApplePayDefault;
    if (
      sectionSavedSPC &&
      billingPaymentMethod === 'APPLE_PAY' &&
      defaultApplePay &&
      !isExtendedPreOrder &&
      isApplePayAcceptable
    ) {
      isApplePayDefault = true;
    }
    return (
      isPaymentDisabled ||
      isPayPalSelected ||
      (isAfterPayInProgress &&
        afterPayMinOrderAmount <= orderTotal &&
        afterPayMaxOrderAmount >= orderTotal) ||
      isVenmoPaymentInProgress ||
      (isBillingAddressSaved &&
        !isAfterPayInProgress &&
        (isCardDetailsSaved || allowWithoutCardNumberPayment)) ||
      isApplePayDefault
    );
  }
);

export const checkDefaultAfterPayEnabled = createSelector(
  [getGrandTotal, getGiftCardsTotal, getAfterPayMaxOrderAmount, getAfterPayMinOrderAmount],
  (grandTotal, giftCardTotal, afterPayMaxOrderAmount, afterPayMinOrderAmount) => {
    const orderTotal = grandTotal - giftCardTotal;
    const defaultAfterPay = getSessionStorage('defaultAfterPay');
    return parseBoolean(
      afterPayMinOrderAmount <= orderTotal &&
        afterPayMaxOrderAmount >= orderTotal &&
        defaultAfterPay
    );
  }
);

export const getOrderContactFormDetails = (state) => {
  return state.singlePageCheckout.contactSection;
};

export const getOrderShippingFormDetails = (state) => {
  return state.singlePageCheckout.shippingSection;
};

export const getOrderPickupFormDetails = (state) => {
  return state.singlePageCheckout.pickupSection;
};

export const getOrderPaymentFormDetails = (state) => {
  return state.singlePageCheckout.paymentSection;
};

export const getSfsSflLoading = (state) => {
  return state.singlePageCheckout.moveItemToSFLoading;
};

export const getTriggerCheckoutWithPayment = (state) => {
  return state.singlePageCheckout.triggerCheckoutWithPayment;
};

export const getRemoveItemInProgress = (state) => {
  return state.singlePageCheckout.removeItemInProgress;
};

export const getShowVerifyCvvModal = (state) => {
  return state.singlePageCheckout.showVerifyCvvModal; // This is set when shipping/pickup is updated
};

export const getShouldShowVerifyCvvModal = (state) => {
  return state.singlePageCheckout.shouldShowVerifyCvvModal; // This is set on click of submit order
};

export const getIsOosItem = (state) => {
  return state.singlePageCheckout.isOosItem;
};

export const getSuppressCCheck = (state) => {
  return state.singlePageCheckout.suppressCCheck; // This is set from BE
};

export const areAllSectionsInCollapsedMode = createSelector(
  [
    (state) => getSinglePageCheckoutSectionState()(state, SPC_SECTION.CONTACT),
    (state) => getSinglePageCheckoutSectionState()(state, SPC_SECTION.SHIPPING),
    (state) => getSinglePageCheckoutSectionState()(state, SPC_SECTION.PICKUP),
    (state) => getSinglePageCheckoutSectionState()(state, SPC_SECTION.PAYMENT),
    (state) => isPickuOrderStatus(state),
    (state) => isECOMStatus(state),
  ],
  (contactSection, shippingSection, pickupSection, paymentSection, isPickUpOrder, isEcomOrder) => {
    let addressDetailsSection = !shippingSection.isExpanded;
    if (isPickUpOrder) {
      addressDetailsSection = !pickupSection.isExpanded;
    } else if (isPickUpOrder && isEcomOrder) {
      addressDetailsSection = !pickupSection.isExpanded && !shippingSection.isExpanded;
    }
    return !contactSection.isExpanded && !paymentSection.isExpanded && addressDetailsSection;
  }
);

export const getExpandedSectionName = createSelector(
  [
    (state) => getSinglePageCheckoutSectionState()(state, SPC_SECTION.CONTACT),
    (state) => getSinglePageCheckoutSectionState()(state, SPC_SECTION.SHIPPING),
    (state) => getSinglePageCheckoutSectionState()(state, SPC_SECTION.PICKUP),
    (state) => getSinglePageCheckoutSectionState()(state, SPC_SECTION.PAYMENT),
  ],
  (contactSection, shippingSection, paymentSection, pickupSection) => {
    if (contactSection.isExpanded) {
      return contactSection.id;
    }
    if (shippingSection.isExpanded) {
      return shippingSection.id;
    }
    if (pickupSection.isExpanded) {
      return pickupSection.id;
    }
    if (paymentSection.isExpanded) {
      return paymentSection.id;
    }
    return null;
  }
);

export const areAllSectionsSaved = createSelector(
  [
    checkIsContactDetailsSaved,
    checkIsShippingDetailsSaved,
    checkIsPaymentSectionSaved,
    checkIsPickupDetailsSaved,
  ],
  (isContactSaved, isShippingSectionSaved, isPaymentSaved, isPickupSectionSaved) =>
    isContactSaved && isShippingSectionSaved && isPaymentSaved && isPickupSectionSaved
);

export const getSfsError = (state) => {
  return state.singlePageCheckout.sfsError;
};

export const isSubmitOrderDisabled = createSelector(
  [
    areAllSectionsSaved,
    areAllSectionsInCollapsedMode,
    BAGPAGE_SELECTORS.isNonCacheBagLoading,
    getSfsError,
  ],
  (allSectionsSaved, allSectionsInCollapsedMode, nonCacheBagLoading, sfsError) =>
    !allSectionsSaved || !allSectionsInCollapsedMode || nonCacheBagLoading || sfsError
);

export const shouldReverifyCvv = createSelector(
  [
    isCvvVisibleOnViewMode,
    CheckoutSelector.getIsPaymentDisabled,
    CheckoutSelector.getCardType,
    CheckoutSelector.isVenmoPaymentInProgress,
  ],
  (isCvvVisible, isPaymentDisabled, cardType, isVenmoPaymentInProgress) =>
    isCvvVisible &&
    !isPaymentDisabled &&
    cardType &&
    cardType !== CARD_TYPES.PLCC_CARD &&
    !isVenmoPaymentInProgress
);

export const getCardTypeRadio = createSelector(getState, (state) => {
  const selector = formValueSelector('cardForm');
  return selector(state, 'cardTypeRadio');
});

export const getCardFormValues = createSelector(
  (state) => getFormValues('cardForm')(state),
  (cardFormValues) => {
    return cardFormValues || {};
  }
);

export const shouldSkipPayment = createSelector(
  [CheckoutSelector.getIsPaymentDisabled, getCardTypeRadio, CheckoutSelector.getIsPayPalSelected],
  (isPaymentDisabled, cardTypeRadio, isPayPalSelected) =>
    isPaymentDisabled || (cardTypeRadio === CheckoutConstants.PAY_PAL && isPayPalSelected)
);

export const getBillingFormSuffix = createSelector(getState, (state) =>
  getCardTypeRadio(state) === CARD_TYPES.PLCC_CARD ? 'PLCC' : ''
);

export const getBillingFormFullName = (state, formName) => {
  const suffix = getBillingFormSuffix(state);
  return formName + suffix;
};

export const getFormState = (state, formCallback) => {
  const isGuestUser = isGuest(state);
  const formName = isGuestUser
    ? CREDIT_CARD_CONSTANTS.GUEST_FORM_NAME
    : CREDIT_CARD_CONSTANTS.FORM_NAME;
  const fullFormName = getBillingFormFullName(state, formName);

  return formCallback(fullFormName)(state);
};

export const getPickupFormData = (state) => state.form && state.form?.PickupForm?.values;
export const clickedOutsideShippingFlag = createSelector(
  getSinglePageCheckoutPageState,
  (state) => {
    return state.isClickedOutsideShipping;
  }
);
export default {
  checkIsSinglePageCheckout,
  checkIsContactDetailsSaved,
  getShippingSectionSavedStatus,
  checkIsPaymentSectionSaved,
  areAllSectionsInCollapsedMode,
};
